/**
 * 登录。支持两段式：口令 →（若已绑 TOTP）验证码。
 *
 * 失败文案原样透传后端的话术。后端对「用户不存在」和「密码错」返回同一句
 * 「邮箱或密码不正确」，这里**不要**自作聪明地细化 —— 那会把邮箱枚举的口子重新打开。
 */

import { useState } from 'react';
import { request, setTokens } from '../lib/api.ts';
import { Button, ErrorBox, Field, TextInput } from '../components/ui.tsx';
import type { LoginResponse } from '../lib/types.ts';

export function LoginPage({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState(false);

  function accept(body: LoginResponse): void {
    if (
      body.access_token === undefined ||
      body.refresh_token === undefined ||
      body.org_id === undefined
    ) {
      setError(new Error('登录响应缺少令牌'));
      return;
    }
    setTokens({
      accessToken: body.access_token,
      refreshToken: body.refresh_token,
      orgId: body.org_id,
    });
    onSignedIn();
  }

  async function submitPassword(): Promise<void> {
    setPending(true);
    setError(null);
    try {
      const body = await request<LoginResponse>('/v1/auth/login', {
        method: 'POST',
        anonymous: true,
        body: { email, password },
      });
      if (body.mfa_required && body.mfa_token !== undefined) {
        setMfaToken(body.mfa_token);
      } else {
        accept(body);
      }
    } catch (cause) {
      setError(cause);
    } finally {
      setPending(false);
    }
  }

  async function submitMfa(): Promise<void> {
    if (mfaToken === null) return;
    setPending(true);
    setError(null);
    try {
      const body = await request<LoginResponse>('/v1/auth/mfa', {
        method: 'POST',
        anonymous: true,
        body: { mfa_token: mfaToken, code },
      });
      accept(body);
    } catch (cause) {
      setError(cause);
      // 挑战令牌有效期很短，验证码错几次后不如让用户从头来
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="login-page">
      <form
        className="login-card"
        onSubmit={(event) => {
          event.preventDefault();
          void (mfaToken === null ? submitPassword() : submitMfa());
        }}
      >
        <div className="login-brand">
          <span className="rail-mark">B</span>
          <div>
            <div className="login-title">BiPay 控制台</div>
            <div className="login-sub">聚合收单与清分</div>
          </div>
        </div>

        {mfaToken === null ? (
          <>
            <Field label="邮箱">
              <TextInput value={email} onChange={setEmail} type="email" placeholder="you@example.com" />
            </Field>
            <Field label="口令">
              <TextInput value={password} onChange={setPassword} type="password" />
            </Field>
          </>
        ) : (
          <Field
            label="二次验证码"
            hint="打开你的身份验证器 App，输入当前 6 位验证码。同一个码只能用一次。"
          >
            <TextInput value={code} onChange={setCode} placeholder="000000" />
          </Field>
        )}

        <ErrorBox error={error} />

        <Button type="submit" variant="primary" disabled={pending}>
          {pending ? '处理中…' : mfaToken === null ? '登录' : '验证'}
        </Button>

        {mfaToken !== null && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setMfaToken(null); setCode(''); setError(null); }}
          >
            返回重新登录
          </Button>
        )}
      </form>
    </div>
  );
}
